import { useEffect, useState, useRef } from 'react';

/**
 * useCountUp - Custom hook for animating number values from start to end
 *
 * Creates smooth 60fps animations using requestAnimationFrame with easeOutQuart easing.
 * Perfect for dashboard statistics and KPI displays.
 *
 * @param end - Target number to count up to
 * @param duration - Animation duration in milliseconds (default: 1500)
 * @param start - Starting number (default: 0)
 * @returns Current animated count value
 *
 * @example
 * ```tsx
 * const count = useCountUp(142, 1500);
 * return <div>{count}</div>; // Animates from 0 to 142
 * ```
 */
export const useCountUp = (
  end: number,
  duration: number = 1500,
  start: number = 0
): number => {
  const [count, setCount] = useState(start);
  const startTimeRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    // Check for prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    // If user prefers reduced motion, skip animation
    if (prefersReducedMotion) {
      setCount(end);
      return;
    }

    // Reset start time for new animation
    startTimeRef.current = null;

    const animate = (currentTime: number) => {
      // Initialize start time on first frame
      if (startTimeRef.current === null) {
        startTimeRef.current = currentTime;
      }

      // Calculate progress (0 to 1)
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Apply easeOutQuart easing for smooth deceleration
      // Formula: 1 - (1 - x)^4
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      // Calculate current count value
      const currentCount = Math.floor(start + (end - start) * easeOutQuart);
      setCount(currentCount);

      // Continue animation if not complete
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure we end on the exact target value
        setCount(end);
      }
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup function
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [end, duration, start]);

  return count;
};
